/**
 * Keep in sync with mh-api/src/lib/smsSegment.js
 * GSM-7: 160 septets. UCS-2: 70 chars.
 */

export const GSM7_SINGLE_SEGMENT = 160;
export const UCS2_SINGLE_SEGMENT = 70;
export const MAX_FIRST_NAME_LEN = 16;
export const FIRST_NAME_PLACEHOLDER = "{{firstName}}";
export const WORST_CASE_FIRST_NAME = "X".repeat(MAX_FIRST_NAME_LEN);
export const SMS_ALERT_SUFFIX =
  " Text (615) 212-9609. Reply STOP to opt out.";

const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";

const GSM7_EXTENDED = "^{}\\[~]|€";
const gsm7Basic = new Set([...GSM7_BASIC]);
const gsm7Extended = new Set([...GSM7_EXTENDED]);

const GSM7_REPLACEMENTS = {
  "\u2018": "'",
  "\u2019": "'",
  "\u201C": '"',
  "\u201D": '"',
  "\u2013": "-",
  "\u2014": "-",
  "\u2026": "...",
  "\u00A0": " ",
  "\t": " ",
};

export const isGsm7Char = (ch) => gsm7Basic.has(ch) || gsm7Extended.has(ch);

export const toGsm7 = (text) => {
  let out = "";
  for (const ch of String(text)) {
    const mapped = GSM7_REPLACEMENTS[ch] ?? ch;
    for (const piece of mapped) {
      if (isGsm7Char(piece)) {
        out += piece;
      }
    }
  }
  return out;
};

const gsmSeptets = (text) => {
  let units = 0;
  for (const ch of String(text)) {
    if (gsm7Extended.has(ch)) {
      units += 2;
      continue;
    }
    if (gsm7Basic.has(ch)) {
      units += 1;
      continue;
    }
    return null;
  }
  return units;
};

export const segmentInfo = (text) => {
  const value = String(text);
  const septets = gsmSeptets(value);
  if (septets != null) {
    return {
      encoding: "gsm7",
      units: septets,
      limit: GSM7_SINGLE_SEGMENT,
      segments: septets === 0 ? 0 : Math.ceil(septets / GSM7_SINGLE_SEGMENT),
    };
  }
  const chars = [...value].length;
  return {
    encoding: "ucs2",
    units: chars,
    limit: UCS2_SINGLE_SEGMENT,
    segments: chars === 0 ? 0 : Math.ceil(chars / UCS2_SINGLE_SEGMENT),
  };
};

export const fitsOneBilledSegment = (text) => segmentInfo(text).segments <= 1;

const clipFirstName = (name) =>
  toGsm7(String(name || "").replace(/\s+/g, " ").trim()).slice(
    0,
    MAX_FIRST_NAME_LEN
  );

const hasReservedFooter = (body) =>
  String(body).endsWith(SMS_ALERT_SUFFIX.trim());

export const applySuffix = (body) => {
  const trimmed = String(body || "").trim();
  if (!trimmed) {
    return SMS_ALERT_SUFFIX.trim();
  }
  if (hasReservedFooter(trimmed)) {
    return trimmed;
  }
  return `${trimmed}${SMS_ALERT_SUFFIX}`;
};

export const assembleJobAlertSms = (draft, firstName) =>
  applySuffix(
    String(draft || "").replaceAll(
      FIRST_NAME_PLACEHOLDER,
      clipFirstName(firstName)
    )
  );

export const fitsDraftForSend = (draft) =>
  fitsOneBilledSegment(assembleJobAlertSms(draft, WORST_CASE_FIRST_NAME));
